import { SectionShell } from './SectionShell';

const DRIVERS = [
  <>
    <strong className="font-semibold text-[#F1F4FA]">
      AI adoption is spreading fast across the profession.
    </strong>{' '}
    Practitioners want it and are using general-purpose models such as ChatGPT,
    Claude, Gemini and Copilot; none are tailored to insolvency.
  </>,
  <>
    <strong className="font-semibold text-[#F1F4FA]">
      The technology has finally caught up.
    </strong>{' '}
    Genuine end-to-end case automation is now possible, not theoretical.
  </>,
  <>
    <strong className="font-semibold text-[#F1F4FA]">
      Case volume remains structurally high
    </strong>
    , with 23,938 company insolvencies in England and Wales in 2025 and no
    AI-native alternative in the market.
  </>,
];

/** The revised opening argument for the main investor pitch. */
export function WhyNow() {
  return (
    <SectionShell
      id="signal"
      index={1}
      eyebrow="Why now"
      title={
        <>
          The industry runs on people, not software.
          <span className="block text-brand-bright">
            We&apos;re building the alternative it&apos;s never had.
          </span>
        </>
      }
      intro="One legacy platform has defined this market for four decades: it’s manual, fragmented, and never built to a modern-day standard. That combination is exactly why the opportunity is now."
    >
      <div className="grid overflow-hidden rounded-2xl border border-[#263352] lg:grid-cols-2">
        <article className="bg-[#0D1526] px-7 py-8 sm:px-10 sm:py-10">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-brand-bright">
            When the industry stays manual
          </p>
          <h3 className="mt-5 text-3xl font-bold leading-tight text-[#F1F4FA]">
            People are the operating system
          </h3>
          <p className="mt-4 max-w-md text-[15.5px] leading-relaxed text-[#8E9FBC]">
            Every case still runs through inboxes, spreadsheets and a single
            legacy platform never built to be replaced. Practitioners hold the
            whole process together by hand, case after case, at real cost to
            their own time.
          </p>
          <div className="mt-7 border-t border-dashed border-[#2C3B5C] pt-4">
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#7387A8]">
              The only player
            </p>
            <p className="mt-2 text-sm text-[#B9C4DB]">
              One incumbent, unchanged for four decades.
            </p>
          </div>
        </article>

        <article className="border-t border-[#263352] bg-[linear-gradient(160deg,#132140,#0F1B33)] px-7 py-8 sm:px-10 sm:py-10 lg:border-l lg:border-t-0">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-brand-bright">
            Why now
          </p>
          <h3 className="mt-5 text-3xl font-bold leading-tight text-[#F1F4FA]">
            Three main drivers, converging now
          </h3>
          <ol className="mt-4">
            {DRIVERS.map((driver, index) => (
              <li
                key={index}
                className="flex gap-4 border-t border-[#223252] py-3 first:border-t-0"
              >
                <span className="w-5 shrink-0 pt-1 text-xs font-extrabold text-brand-bright">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="text-[15.5px] leading-relaxed text-[#D3DCEC]">
                  {driver}
                </span>
              </li>
            ))}
          </ol>
          <p className="mt-4 text-sm leading-relaxed text-[#7387A8]">
            Designed to replace the only platform this market has ever had.
          </p>
        </article>
      </div>

      <p className="mt-8 text-xl leading-relaxed text-[#9DAFC9] sm:text-[21px]">
        The opportunity:{' '}
        <strong className="font-bold text-[#F1F4FA]">
          become the only complete, AI-grounded, end-to-end solution in a market
          that has never had one.
        </strong>
      </p>

      <p className="mt-6 text-xs text-ink-dim">
        2025 company insolvency count:{' '}
        <a
          href="https://www.gov.uk/government/statistics/company-insolvencies-december-2025"
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-2 hover:text-ink-muted"
        >
          Insolvency Service, England and Wales
        </a>
      </p>
    </SectionShell>
  );
}
